import React from 'react';
import {
  C,
  CARD_W,
  SUB_BOMB_DISPLAY_H,
  nx,
  ny,
  imgUrl,
  normalizeImgField,
} from '../flowCanvas.constants';

const HEADER_H = 40;
const LABEL_X = 12;
const CONTROL_X = 88;
const CONTROL_W = CARD_W - CONTROL_X - 12;
const IMAGE_BOX_H = 106;

const DISPLAY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
];

function SelectControl({ x, y, value, options, onChange }) {
  return (
    <foreignObject x={x} y={y} width={CONTROL_W} height="24" style={{ pointerEvents: 'auto' }}>
      <select
        value={value}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onChange?.(e.target.value);
        }}
        style={{
          width: '100%',
          height: '22px',
          borderRadius: '11px',
          border: '1px solid rgba(145,70,255,0.35)',
          background: 'rgba(145,70,255,0.10)',
          color: '#ffffff',
          fontSize: '9px',
          fontWeight: 700,
          padding: '0 8px',
          outline: 'none',
          boxSizing: 'border-box',
          cursor: 'pointer',
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ background: '#1a1a2e', color: '#fff' }}>
            {opt.label}
          </option>
        ))}
      </select>
    </foreignObject>
  );
}

export default function SubBombDisplaySettingsNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
  assetFolder,
  campaignId,
}) {
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top = y - SUB_BOMB_DISPLAY_H / 2;

  const cfg = step?.customisationConfig;
  const tabKey = cfg?.tabKey;
  const varKey = cfg?.varKey;
  const settings = liveCustomisationData?.[tabKey]?.[varKey]?.settings?.[0]
    ?? step?.populatedData?.variables?.find((v) => v.variableName === 'settings')?.allValues?.[0]
    ?? {};
  const copyVals = liveCustomisationData?.[tabKey]?.[varKey]?.copy?.[0]
    ?? step?.populatedData?.variables?.find((v) => v.variableName === 'copy')?.allValues?.[0]
    ?? {};
  const imageVals = liveCustomisationData?.[tabKey]?.[varKey]?.image?.[0]
    ?? step?.populatedData?.variables?.find((v) => v.variableName === 'image')?.allValues?.[0]
    ?? {};

  const displayType = settings?.displayType ?? 'text';
  const imageField = normalizeImgField(imageVals?.filename);
  const imageSrc = imageField?.srcUrl
    ? imageField.srcUrl
    : imageField?.name ? imgUrl(imageField.name, imageField.isDefault, assetFolder, campaignId) : null;
  const imageName = imageField?.name || 'No image selected';
  const copyValue = copyVals?.copy ?? '';
  const modeLabel = displayType === 'image'
    ? 'Current Image'
    : displayType === 'text'
      ? 'Live Copy'
      : 'Display Disabled';
  const imageBoxY = top + HEADER_H + 42;

  return (
    <g className="fc-node fc-node--subbomb-display-settings" onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={`${step.stepId}-subbomb-image-clip`}>
          <rect x={left + 12} y={imageBoxY} width={CARD_W - 24} height={IMAGE_BOX_H} rx="8" />
        </clipPath>
        <pattern id={`${step.stepId}-subbomb-image-checker`} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#555" />
          <rect width="4" height="4" fill="#333" />
          <rect x="4" y="4" width="4" height="4" fill="#333" />
        </pattern>
      </defs>
      <rect x={left + 2} y={top + 4} width={CARD_W} height={SUB_BOMB_DISPLAY_H} rx="10" fill="#000" fillOpacity="0.3" />
      <rect
        x={left}
        y={top}
        width={CARD_W}
        height={SUB_BOMB_DISPLAY_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />
      <line x1={left + 1} y1={top + HEADER_H} x2={left + CARD_W - 1} y2={top + HEADER_H} stroke={C.border} strokeWidth="1" />

      <text x={left + 14} y={top + 24} fontSize="11.5" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Display Settings'}
      </text>
      <text x={left + 14} y={top + 37} fontSize="8.5" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Choose text, image or none
      </text>

      <text x={left + LABEL_X} y={top + HEADER_H + 17} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Display
      </text>
      <SelectControl
        x={left + CONTROL_X}
        y={top + HEADER_H + 4}
        value={displayType}
        options={DISPLAY_OPTIONS}
        onChange={(value) => onFieldChange?.('settings', 'displayType', value)}
      />

      <text x={left + LABEL_X} y={top + HEADER_H + 50} fontSize="9" fontWeight="700" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {modeLabel}
      </text>

      {displayType === 'text' && (
        <foreignObject x={left + 12} y={top + HEADER_H + 58} width={CARD_W - 24} height="30" style={{ pointerEvents: 'auto' }}>
          <input
            xmlns="http://www.w3.org/1999/xhtml"
            value={copyValue}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => onFieldChange?.('copy', 'copy', e.target.value)}
            placeholder="Enter copy…"
            style={{
              width: '100%',
              height: '28px',
              borderRadius: '8px',
              border: '1px solid rgba(145,70,255,0.18)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              padding: '0 10px',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
        </foreignObject>
      )}

      {displayType === 'image' && (
        <>
          <rect x={left + 12} y={imageBoxY} width={CARD_W - 24} height={IMAGE_BOX_H} rx="8" fill={`url(#${step.stepId}-subbomb-image-checker)`} />
          {imageSrc ? (
            <image
              href={imageSrc}
              x={left + 12}
              y={imageBoxY}
              width={CARD_W - 24}
              height={IMAGE_BOX_H}
              preserveAspectRatio="xMidYMid meet"
              clipPath={`url(#${step.stepId}-subbomb-image-clip)`}
              style={{ pointerEvents: 'none' }}
            />
          ) : null}
          <rect x={left + 12} y={imageBoxY} width={CARD_W - 24} height={IMAGE_BOX_H} rx="8" fill="none" stroke="rgba(145,70,255,0.28)" strokeWidth="1" />
          <rect x={left + 16} y={imageBoxY + IMAGE_BOX_H - 16} width={CARD_W - 32} height="12" rx="6" fill="#000" fillOpacity="0.58" />
          <text x={left + CARD_W / 2} y={imageBoxY + IMAGE_BOX_H - 7} textAnchor="middle" fontSize="8" fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {imageName.length > 26 ? `${imageName.slice(0, 26)}...` : imageName}
          </text>
        </>
      )}

      {displayType === 'none' && (
        <text x={left + CARD_W / 2} y={top + HEADER_H + 77} textAnchor="middle" fontSize="9.5" fill={C.textDim} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          Nothing will be shown for this element
        </text>
      )}
    </g>
  );
}